/**
 * Display formatting.
 *
 * Formatters here return `null` for absent values rather than substituting a
 * zero or a dash, so callers are forced to decide how "we do not know" should
 * look. Spec section 56: unknown is not zero.
 *
 * Everything is locale-aware through `intlLocale()` rather than through an
 * explicit argument. These are called from a few hundred places, most of them
 * inside JSX, and a locale parameter at every call site is a parameter that
 * will eventually be forgotten at one of them — leaving a single English
 * "20.4M" in a Korean table with nothing to catch it.
 */

import { intlLocale, tr } from '../i18n';

/**
 * `Intl` formatters are expensive to construct, so they are built once per
 * locale and kept. Two locales means this map never grows past two entries.
 */
const compactByLocale = new Map<string, Intl.NumberFormat>();
const plainByLocale = new Map<string, Intl.NumberFormat>();

function compact(): Intl.NumberFormat {
  const locale = intlLocale();
  let formatter = compactByLocale.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 2 });
    compactByLocale.set(locale, formatter);
  }
  return formatter;
}

function plain(): Intl.NumberFormat {
  const locale = intlLocale();
  let formatter = plainByLocale.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale);
    plainByLocale.set(locale, formatter);
  }
  return formatter;
}

/** 20_400_000 -> "20.4M" / "2040만". Used for power, damage, kills. */
export function formatCompact(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return compact().format(value);
}

export function formatNumber(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return plain().format(value);
}

/** Signed compact delta, e.g. "+8.3M" / "-1.2M", "+830만" / "-120만". */
export function formatDelta(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const sign = value > 0 ? '+' : '';
  return `${sign}${compact().format(value)}`;
}

/**
 * @param value A ratio (0.062), not an already-multiplied percentage.
 */
export function formatPercent(value: number | null | undefined, digits = 1): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const sign = value > 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(digits)}%`;
}

export function formatRatioAsPercent(value: number | null | undefined, digits = 0): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return `${(value * 100).toFixed(digits)}%`;
}

/**
 * ko-KR renders this as "2026년 8월 25일 01:18". `hour12` is forced off in both
 * locales so a rally time is never ambiguous by two hours on a screen an
 * operator reads at 1am.
 */
export function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(intlLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(intlLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Seconds -> "3m 20s" / "3분 20초". Used for rally return cycle times. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return null;
  const units = tr().duration;
  const total = Math.round(seconds);
  if (total < 60) return units.seconds(total);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes < 60) {
    return rest === 0 ? units.minutes(minutes) : units.minutesSeconds(minutes, rest);
  }
  const hours = Math.floor(minutes / 60);
  return units.hoursMinutes(hours, minutes % 60);
}

/**
 * Town centre level. Jeabs calls it "town hall"; in game it reads as TC, in
 * both languages — Korean players say "TC30" out loud, so this is not
 * translated.
 */
export function formatTc(level: number | null | undefined): string | null {
  if (level === null || level === undefined) return null;
  return `TC${level}`;
}

/** 1 -> "1st", 72 -> "72nd", 11 -> "11th". English ordinal suffixes. */
export function ordinal(value: number): string {
  const rounded = Math.round(value);
  const lastTwo = Math.abs(rounded) % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${rounded}th`;
  switch (Math.abs(rounded) % 10) {
    case 1:
      return `${rounded}st`;
    case 2:
      return `${rounded}nd`;
    case 3:
      return `${rounded}rd`;
    default:
      return `${rounded}th`;
  }
}

/**
 * A percentile position.
 *
 * English uses the ordinal ("72nd"); Korean has no ordinal suffix and would
 * read "72번째" as a rank out of some unstated total, which is a different
 * claim. The Korean form names the scale instead.
 */
export function formatPercentile(value: number): string {
  return tr().percentile(Math.round(value), ordinal(value));
}

/**
 * Game and product acronyms that must survive humanizing. Without these,
 * 'TC_UPGRADE' reads as 'Tc upgrade', which looks like a typo to the operator
 * who says "TC" out loud twenty times a night.
 */
const ACRONYMS = new Set(['tc', 'vip', 'kvk', 'ac', 'pvp', 'r4', 'r5']);

/**
 * Turns 'PLAYER_TRANSFERRED_ALLIANCE' into 'Player transferred alliance'.
 *
 * This is the fallback for an enum member no dictionary knows about — a value
 * the API started sending before this app learned its name. Showing the raw id
 * made readable is honest; guessing a translation for it would not be.
 */
export function humanizeEnum(value: string): string {
  const words = value.toLowerCase().split('_').filter(Boolean);
  if (words.length === 0) return value;
  return words
    .map((word, index) => {
      if (ACRONYMS.has(word)) return word.toUpperCase();
      if (index === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      return word;
    })
    .join(' ');
}

/**
 * Parses an optional numeric form field.
 *
 * Blank means "not recorded" and stays `null`. This matters for Bear troop
 * counts (spec section 24.3): the operator frequently cannot see a joiner's
 * troop split, and coercing that blank to 0 would poison every damage-per-troop
 * average computed from it.
 */
export function parseOptionalInt(raw: string): number | null | 'INVALID' {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed.replace(/[,\s]/g, ''))) return 'INVALID';
  const parsed = Number.parseInt(trimmed.replace(/[,\s]/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : 'INVALID';
}
